/**
 * Global Privacy Control (GPC) Detection Utility
 * 
 * Implements detection of the Global Privacy Control browser signal
 * as specified in the W3C specification: https://www.w3.org/TR/gpc/
 * 
 * GPC is a browser setting that signals a user's opt-out preference
 * for the sale or sharing of their personal information.
 */

/**
 * Type definition for GPC detection result
 */
export interface GpcDetectionResult {
  /** Whether GPC signal is currently active */
  isActive: boolean;
  /** Whether the browser supports GPC detection */
  isSupported: boolean;
  /** Timestamp of when the detection was performed */
  detectedAt: number;
}

/**
 * Type definition for GPC state change callback
 */
export type GpcStateChangeCallback = (isActive: boolean) => void;

/**
 * Check if the browser supports GPC detection
 * 
 * @returns {boolean} True if navigator.globalPrivacyControl is available
 */
export const isGpcSupported = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  // Check for navigator.globalPrivacyControl property
  // This is the standard way to detect GPC support
  return 'globalPrivacyControl' in navigator;
};

/**
 * Detect if GPC signal is currently active
 * 
 * @returns {GpcDetectionResult} Object containing detection result and metadata
 */
export const detectGpc = (): GpcDetectionResult => {
  const isSupported = isGpcSupported();
  let isActive = false;

  if (isSupported) {
    try {
      // navigator.globalPrivacyControl is a boolean property
      // true means GPC is active, false or undefined means it's not
      isActive = (navigator as any).globalPrivacyControl === true;
    } catch (error) {
      // If there's an error accessing the property, assume GPC is not active
      console.warn('Error detecting GPC signal:', error);
      isActive = false;
    }
  }

  return {
    isActive,
    isSupported,
    detectedAt: Date.now(),
  };
};

/**
 * Check if GPC is currently active (convenience function)
 * 
 * @returns {boolean} True if GPC signal is active
 */
export const isGpcActive = (): boolean => {
  return detectGpc().isActive;
};

/**
 * Monitor GPC state changes during browsing session
 * 
 * Note: GPC is typically set at browser startup and doesn't change during
 * a session, but this function provides a mechanism to check periodically
 * or on navigation events.
 * 
 * @param {GpcStateChangeCallback} callback Function to call when GPC state changes
 * @param {number} checkInterval Optional interval in milliseconds to check for changes (default: 5000ms)
 * @returns {() => void} Cleanup function to stop monitoring
 */
export const monitorGpcChanges = (
  callback: GpcStateChangeCallback,
  checkInterval: number = 5000
): (() => void) => {
  if (!isGpcSupported()) {
    // If GPC is not supported, call callback with false and return no-op cleanup
    callback(false);
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {};
  }

  let previousState: boolean | null = null;
  let intervalId: NodeJS.Timeout | null = null;

  const checkGpcState = (): void => {
    const currentState = isGpcActive();

    // Only call callback if state has changed
    if (previousState !== null && previousState !== currentState) {
      callback(currentState);
    }

    previousState = currentState;
  };

  // Initial check
  previousState = isGpcActive();
  callback(previousState);

  // Set up periodic checking
  intervalId = setInterval(checkGpcState, checkInterval);

  // Return cleanup function
  return (): void => {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
};

/**
 * Get browser metadata for analytics tracking
 * 
 * @returns {Record<string, unknown>} Object containing browser/device metadata
 */
export const getGpcBrowserMetadata = (): Record<string, unknown> => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {};
  }

  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    gpcSupported: isGpcSupported(),
    gpcActive: isGpcActive(),
  };
};

